"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { TextField, TextareaField, SwitchField, S3UploadField } from "@admin/_components/form-fields";
import { useApiPost } from "@admin/_lib/api-hooks";

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Combine a date string (YYYY-MM-DD) and time string (HH:MM) into an epoch ms timestamp.
 * Returns null if either part is missing or invalid.
 */
function combineDateTime(date: string, time: string): number | null {
  if (!date || !time) return null;
  const dt = new Date(`${date}T${time}`);
  return isNaN(dt.getTime()) ? null : dt.getTime();
}

/**
 * Split an epoch ms timestamp into { date: "YYYY-MM-DD", time: "HH:MM" }.
 */
function splitDateTime(ts: number | null | undefined): { date: string; time: string } {
  if (!ts) return { date: "", time: "" };
  const d = new Date(ts);
  if (isNaN(d.getTime())) return { date: "", time: "" };
  const date = d.toISOString().slice(0, 10);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return { date, time: `${hh}:${mm}` };
}

function getBaseUrl() {
  return process.env.NEXT_PUBLIC_API_CLOUDFRONT ?? "https://api.divineinfinitebeing.com/";
}

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z.object({
  title: z.string().min(1, "Title is required"),
  priority: z.any(),
  start_date: z.string().optional().default(""),
  start_time: z.string().optional().default(""),
  end_date: z.string().optional().default(""),
  end_time: z.string().optional().default(""),
  description: z.string().optional().default(""),
  theme_image: z.array(z.any()).default([]),
  icon_image: z.array(z.any()).default([]),
  assets: z.array(z.any()).default([]),
  status: z.boolean().default(true),
});

type FormValues = z.infer<typeof schema>;

// ── WheelSignForm ─────────────────────────────────────────────────────────────

export function WheelSignForm({ id }: { id?: string }) {
  const router = useRouter();
  const isEdit = !!id;
  const { data: session } = useSession();
  const baseUrl = getBaseUrl();

  const { data: record, isLoading: isFetching } = useQuery({
    queryKey: ["wheel-sign-single", id],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}wheel_signs/wheel-sign-single-fetch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({ _id: id }),
      });
      const data = await res.json();
      const result = data.results?.res ?? data.results ?? data.res ?? data;
      return Array.isArray(result) ? result[0] : result;
    },
    enabled: isEdit && !!session?.token,
  });

  const mutation = useApiPost(
    isEdit ? "wheel_signs/wheel-sign-update" : "wheel_signs/wheel-sign-add",
    { invalidateKeys: ["wheel_signs/wheel-sign-list"] }
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: {
      title: "",
      priority: "",
      start_date: "",
      start_time: "",
      end_date: "",
      end_time: "",
      description: "",
      theme_image: [],
      icon_image: [],
      assets: [],
      status: true,
    },
  });

  // WS-03: Hydrate form — split stored startDateTime/endDateTime back into date + time
  useEffect(() => {
    if (record) {
      const start = splitDateTime(record.startDateTime as number);
      const end   = splitDateTime(record.endDateTime as number);

      const themeArr = Array.isArray(record.theme_image)
        ? record.theme_image
        : record.theme_image ? [record.theme_image] : [];
      const iconArr = Array.isArray(record.icon_image)
        ? record.icon_image
        : record.icon_image ? [record.icon_image] : [];
      const assetsArr = Array.isArray(record.assets)
        ? record.assets
        : record.assets ? [record.assets] : [];

      form.reset({
        title: String(record.title ?? ""),
        priority: record.priority != null ? String(record.priority) : "",
        start_date: start.date,
        start_time: start.time,
        end_date: end.date,
        end_time: end.time,
        description: String(record.description ?? ""),
        theme_image: themeArr,
        icon_image: iconArr,
        assets: assetsArr,
        status: record.status === 1 || record.status === true,
      });
    }
  }, [record, form]);

  async function onSubmit(values: FormValues) {
    try {
      // WS-03: combine date+time into startDateTime/endDateTime epoch ms
      const startDateTime = combineDateTime(values.start_date ?? "", values.start_time ?? "");
      const endDateTime   = combineDateTime(values.end_date ?? "", values.end_time ?? "");

      const body: Record<string, unknown> = {
        title: values.title,
        priority: isNaN(parseFloat(String(values.priority)))
          ? values.priority
          : parseFloat(String(values.priority)),
        description: values.description,
        theme_image: values.theme_image,
        icon_image: values.icon_image,
        assets: values.assets,
        status: values.status ? 1 : 0,
      };

      if (startDateTime != null) body.startDateTime = startDateTime;
      if (endDateTime   != null) body.endDateTime   = endDateTime;

      // WS-09: Angular edit uses `id` (not `_id`) as the identifier for updates
      if (isEdit) body.id = id;

      await mutation.mutateAsync(body as any);
      toast.success(isEdit ? "Updated successfully" : "Created successfully");
      router.push("/admin/astrology/wheel-signs");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Operation failed");
    }
  }

  if (isEdit && isFetching) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="size-9 rounded-md" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => router.back()}>
          <ArrowLeft className="size-4" />
        </Button>
        <div className="h-6 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? "Edit Wheel Sign" : "Add Wheel Sign"}
          </h1>
        </div>
      </div>

      <Card>
        <CardHeader className="border-b" style={{ borderColor: "hsl(240 20% 20%)" }}>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
            Wheel Sign Details
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <TextField
                    control={form.control}
                    name="title"
                    label="Title"
                    placeholder="Sign title"
                  />
                </div>

                <TextField
                  control={form.control}
                  name="priority"
                  label="Priority"
                  type="number"
                  placeholder="0"
                />

                {/* Start Date + Time */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">Start Date</label>
                  <input
                    type="date"
                    {...form.register("start_date")}
                    className="w-full rounded-md border px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1"
                    style={{ background: "hsl(240 20% 12%)", borderColor: "hsl(240 20% 26%)" }}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">Start Time</label>
                  <input
                    type="time"
                    {...form.register("start_time")}
                    className="w-full rounded-md border px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1"
                    style={{ background: "hsl(240 20% 12%)", borderColor: "hsl(240 20% 26%)" }}
                  />
                </div>

                {/* End Date + Time */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">End Date</label>
                  <input
                    type="date"
                    {...form.register("end_date")}
                    className="w-full rounded-md border px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1"
                    style={{ background: "hsl(240 20% 12%)", borderColor: "hsl(240 20% 26%)" }}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">End Time</label>
                  <input
                    type="time"
                    {...form.register("end_time")}
                    className="w-full rounded-md border px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1"
                    style={{ background: "hsl(240 20% 12%)", borderColor: "hsl(240 20% 26%)" }}
                  />
                </div>

                <div className="md:col-span-2">
                  <TextareaField
                    control={form.control}
                    name="description"
                    label="Description"
                    placeholder="Sign description..."
                  />
                </div>

                {/* WS-03: Theme image S3 upload */}
                <div>
                  <S3UploadField
                    control={form.control}
                    name="theme_image"
                    label="Theme Image"
                    bucket="all-frontend-assets"
                    path="divine-infinity-being/wheel_sign_images/"
                    accept="image/*"
                  />
                </div>

                {/* WS-04: Icon image S3 upload */}
                <div>
                  <S3UploadField
                    control={form.control}
                    name="icon_image"
                    label="Icon Image"
                    bucket="all-frontend-assets"
                    path="divine-infinity-being/wheel_sign_images/"
                    accept="image/*"
                  />
                </div>

                {/* WS-02: Assets upload */}
                <div className="md:col-span-2">
                  <S3UploadField
                    control={form.control}
                    name="assets"
                    label="Assets"
                    bucket="all-frontend-assets"
                    path="divine-infinity-being/wheel_sign_assets/"
                  />
                </div>

                <div className="md:col-span-2">
                  <SwitchField
                    control={form.control}
                    name="status"
                    label="Active"
                    description="Make this wheel sign visible"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed btn-orange"
                >
                  {mutation.isPending ? "Saving..." : isEdit ? "Update" : "Create"}
                </button>
                <Button type="button" variant="outline" onClick={() => router.back()}>
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
